import { useState } from 'react';
import type { HabitRow } from '@st/contract';
import {
  HABIT_WINDOW_DAYS,
  useCreateHabit,
  useDeleteHabit,
  useHabits,
  useLogHabit,
} from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { formatDay } from '../../lib/format';
import { HABIT_KEY_PATTERN, habitGrid, habitIntensity, slugifyHabitKey, sortHabits } from '../../lib/habits';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  HabitGrid,
  Input,
  Modal,
  NumberInput,
  SkeletonList,
  useToast,
} from '../../ui';
import { IconFlame } from '../../components/icons';

/**
 * Navike: niz, današnja oznaka i mreža zadnjih 30 dana.
 * Mreža se gradi u klijentu jer server vraća samo ISPUNJENE dane — rupe su
 * jednako važan podatak kao ispunjeni kvadratići.
 */
export function Habits() {
  const { t, locale } = useTranslation();
  const { toast } = useToast();
  const habits = useHabits();
  const log = useLogHabit();
  const create = useCreateHabit();
  const remove = useDeleteHabit();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [target, setTarget] = useState<number | ''>('');
  const [unit, setUnit] = useState('');
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [pendingRemove, setPendingRemove] = useState<HabitRow | null>(null);

  const to = habits.data?.to ?? new Date().toISOString().slice(0, 10);

  async function onCreate(): Promise<void> {
    const label = name.trim();
    if (!label) {
      setNameError(t('habits.nameRequired'));
      return;
    }
    const key = slugifyHabitKey(label);
    if (!HABIT_KEY_PATTERN.test(key)) {
      setNameError(t('habits.invalidKey'));
      return;
    }
    try {
      await create.mutateAsync({
        key,
        labelHr: label,
        ...(target === '' ? {} : { targetValue: target }),
        ...(unit.trim() ? { unit: unit.trim() } : {}),
      });
      setCreateOpen(false);
      setName('');
      setTarget('');
      setUnit('');
      setNameError(undefined);
      toast(t('habits.created'), 'success');
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  async function onRemove(): Promise<void> {
    if (!pendingRemove) return;
    try {
      await remove.mutateAsync(pendingRemove.id);
      setPendingRemove(null);
      toast(t('habits.removed'), 'success');
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  return (
    <>
      <PageHeader
        title={t('habits.title')}
        action={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            {t('habits.create')}
          </Button>
        }
      />

      <QueryBoundary
        query={habits}
        skeleton={<SkeletonList rows={3} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{
          message: t('habits.empty'),
          actionLabel: t('habits.create'),
          onAction: () => setCreateOpen(true),
        }}
      >
        {(data) => (
          <ul className="flex flex-col gap-4">
            {sortHabits(data.items).map((habit) => {
              const cells = habitGrid(habit.history, data.to, HABIT_WINDOW_DAYS);
              return (
                <li key={habit.id}>
                  <Card as="article">
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="font-display text-[18px] font-semibold text-ink">
                          {habit.label}
                        </h2>
                        <p className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px] text-ink-muted">
                          <span className="inline-flex items-center gap-1 text-accent-dark">
                            <IconFlame width={15} height={15} />
                            {t('habits.streakDays', { n: habit.streak })}
                          </span>
                          <span aria-hidden="true">·</span>
                          <span>{t('habits.completion', { n: habit.completionRate })}</span>
                          {habit.targetValue !== null && (
                            <>
                              <span aria-hidden="true">·</span>
                              <span>
                                {t('habits.target')}: {habit.targetValue} {habit.unit ?? ''}
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {habit.doneToday && <Badge tone="success">{t('habits.done')}</Badge>}
                        <Button
                          size="sm"
                          variant={habit.doneToday ? 'secondary' : 'primary'}
                          onClick={() =>
                            log.mutate(
                              { id: habit.id, loggedOn: to, done: !habit.doneToday },
                              { onError: (err) => toast(toErrorMessage(t, err).title, 'danger') },
                            )
                          }
                        >
                          {habit.doneToday ? t('habits.unmarkToday') : t('habits.markToday')}
                        </Button>
                      </div>
                    </div>

                    <p className="eyebrow mb-2">{t('habits.lastDays', { n: HABIT_WINDOW_DAYS })}</p>
                    <div className="overflow-x-auto">
                      <HabitGrid
                        cells={cells}
                        ariaLabel={t('habits.gridLabel', {
                          n: HABIT_WINDOW_DAYS,
                          name: habit.label,
                        })}
                        intensityOf={(cell) => habitIntensity(cell, habit.targetValue)}
                        titleOf={(cell) =>
                          `${formatDay(`${cell.day}T00:00:00.000Z`, locale)} — ${
                            cell.done ? t('habits.done') : t('habits.notDone')
                          }`
                        }
                      />
                    </div>

                    <div className="mt-4 flex justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setPendingRemove(habit)}>
                        {t('habits.remove')}
                      </Button>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </QueryBoundary>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('habits.create')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={create.isPending} onClick={() => void onCreate()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            id="habit-name"
            label={t('habits.name')}
            hint={t('habits.keyHint')}
            value={name}
            error={nameError}
            onChange={(e) => {
              setName(e.target.value);
              setNameError(undefined);
            }}
          />
          <div className="grid grid-cols-2 gap-4">
            <NumberInput
              id="habit-target"
              label={t('habits.target')}
              value={target}
              onValueChange={setTarget}
            />
            <Input
              id="habit-unit"
              label={t('habits.unit')}
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingRemove !== null}
        title={t('habits.remove')}
        question={t('habits.removeQuestion', { name: pendingRemove?.label ?? '' })}
        confirmLabel={t('action.confirmDelete')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={remove.isPending}
        onConfirm={() => void onRemove()}
        onClose={() => setPendingRemove(null)}
      />
    </>
  );
}
